import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { aiBillingService } from '../../../services/aiBillingService';
import { getProviderConfig } from '../../../config/aiProviders';

const fmtUSD = (n) => {
  if (typeof n !== 'number' || isNaN(n)) return '$0.00';
  return n < 0.01 && n > 0 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
};

// Presentation for every statusLevel the ai-billing-status edge function can
// return. `dot`/`label` drive the 🟢🟡🔴 badge; `banner` drives the
// colored strip + message row (never silently shown as "Connected" for
// anything but a genuinely healthy/connected state).
const STATUS_META = {
  healthy: { dot: 'bg-emerald-500', label: 'Connected', banner: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: 'CheckCircle' },
  connected: { dot: 'bg-emerald-500', label: 'Connected', banner: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: 'CheckCircle' },
  low: { dot: 'bg-amber-500', label: 'Low balance', banner: 'bg-amber-50 border-amber-200 text-amber-700', icon: 'AlertTriangle' },
  critical: { dot: 'bg-red-500', label: 'Critical', banner: 'bg-red-50 border-red-200 text-red-700', icon: 'AlertTriangle' },
  no_balance: { dot: 'bg-red-500', label: 'No balance', banner: 'bg-red-50 border-red-200 text-red-700', icon: 'AlertCircle' },
  invalid: { dot: 'bg-red-500', label: 'Invalid credential', banner: 'bg-red-50 border-red-200 text-red-700', icon: 'AlertCircle' },
  unavailable: { dot: 'bg-slate-400', label: 'Unavailable', banner: 'bg-slate-50 border-slate-200 text-slate-600', icon: 'AlertTriangle' },
  not_connected: { dot: 'bg-slate-400', label: 'Not connected', banner: 'bg-slate-50 border-slate-200 text-slate-600', icon: 'AlertCircle' },
};

const AIBilling = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    const { data, error: err } = await aiBillingService.getStatus();
    if (err) setError(err.message);
    else setError(null);
    setStatus(data || null);
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => { if (user?.id) load(); }, [user?.id]);

  const meta = STATUS_META[status?.statusLevel] || STATUS_META.not_connected;
  const providerConfig = status?.provider ? getProviderConfig(status.provider) : null;
  const providerLabel = providerConfig?.label || (status?.provider ? status.provider : null);

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#DCFCE7' }}>
            <Icon name="Brain" size={15} style={{ color: '#10A37F' }} />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#0F172A]">AI Billing</p>
            <p className="text-[11px] text-[#94A3B8]">
              {providerLabel ? `Active provider: ${providerLabel}` : 'No active provider'}
            </p>
          </div>
        </div>
        <button
          onClick={() => load(true)}
          disabled={loading || refreshing}
          className="text-xs text-[#64748B] hover:text-[#0F172A] transition-colors flex items-center gap-1 disabled:opacity-50"
        >
          <Icon name="RefreshCw" size={12} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-100 rounded w-full" />
          <div className="h-8 bg-slate-100 rounded w-full" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium bg-red-50 border-red-200 text-red-600">
          <Icon name="AlertCircle" size={13} />
          Couldn't load AI billing status: {error}
        </div>
      ) : !status?.connected && !status?.provider ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium bg-slate-50 border-slate-200 text-slate-600">
            <Icon name="AlertCircle" size={13} />
            No AI provider connected yet.
          </div>
          <Link
            to="/tools/settings"
            className="block text-center text-xs font-semibold bg-emerald-600 text-white rounded-lg py-2 hover:bg-emerald-700 transition-colors"
          >
            Connect a provider in Settings
          </Link>
        </div>
      ) : (
        <>
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${meta.banner}`}>
            <Icon name={meta.icon} size={13} />
            {status.message || meta.label}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-xs text-[#64748B] mb-0.5">Provider</p>
              <p className="text-sm font-semibold text-[#0F172A]">{providerLabel || '—'}</p>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-xs text-[#64748B] mb-0.5">Model</p>
              <p className="text-sm font-semibold text-[#0F172A] truncate">{status.model || '—'}</p>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-xs text-[#64748B] mb-0.5">Status</p>
              <p className="text-sm font-semibold text-[#0F172A] flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                {meta.label}
              </p>
            </div>
            {status.balance?.supported ? (
              <div className="bg-[#F8FAFC] rounded-lg p-3">
                <p className="text-xs text-[#64748B] mb-0.5">Available Balance</p>
                <p className="text-sm font-bold text-[#0F172A]">
                  {status.balance.amount === null ? 'Unavailable' : fmtUSD(status.balance.amount)}
                  {status.balance.currency ? ` ${status.balance.currency}` : ''}
                </p>
              </div>
            ) : (
              <div className="bg-[#F8FAFC] rounded-lg p-3">
                <p className="text-xs text-[#64748B] mb-0.5">Balance</p>
                <p className="text-xs text-[#94A3B8]">Not shown for {providerLabel || 'this provider'}</p>
              </div>
            )}
          </div>

          <Link
            to="/tools/settings"
            className="text-xs text-[#64748B] hover:text-[#0F172A] transition-colors text-center"
          >
            Manage AI providers in Settings →
          </Link>
        </>
      )}
    </div>
  );
};

export default AIBilling;
